import React from "react";
import { IconInfoCircle, IconTrash } from "@tabler/icons-react";
import { componentsStore } from "store/Mapping/ComponentsStore";
import clsx from "clsx";
import ProductComponentInfo from "features/MappingTools/ProductComponentInfo";
import { observer } from "mobx-react-lite";
import { ComponentDetailsByFileOut, ProductSearchOutput } from "api-client";
import ProductDetailsPopup from "../ProductDetailsPopup";

interface ComponentChildContentProps {
  childItem: ComponentDetailsByFileOut;
  removable?: boolean;
  hideInfoIcon?: boolean;
  className?: string;
  reorderOptions?: React.ReactElement;
  dragHandle?: React.ReactElement;
  moveUp?: () => void;
  moveDown?: () => void;
}

const ComponentChildContent = ({
  childItem,
  removable,
  hideInfoIcon,
  className,
  ...props
}: ComponentChildContentProps) => {
  const [openDetails, setOpenDetails] = React.useState(false);
  const removeRow = (child: ComponentDetailsByFileOut) => {
    componentsStore.onChangeData(
      "childrenList",
      componentsStore.data.childrenList.filter((item) => child != item)
    );
    componentsStore.getComponentCompliance();
    componentsStore.getComponentDINCategory();
  };

  function showDetailsIcon() {
    return (
      <IconInfoCircle
        className={clsx(
          "text-gray-500 hover:text-gray-700 w-6 h-6 cursor-pointer",
          {
            "text-indigo-700 hover:text-indigo-500": removable,
          }
        )}
        onClick={() => setOpenDetails(true)}
      />
    );
  }

  function hoverActions() {
    return (
      <div className="hidden absolute w-full left-0 right-0 h-full top-0 transition group-hover/row:flex justify-between">
        <div className="flex">
          <div className="flex items-center bg-white pl-4">
            {props.dragHandle}
          </div>
          <div className="w-10 bg-gradient-to-l from-transparent to-white"></div>
        </div>
        <div className="flex">
          <div className="w-10 bg-gradient-to-r from-transparent to-white"></div>
          <div className="flex items-center bg-white pr-4">
            {props.reorderOptions}
            {hideInfoIcon ? null : showDetailsIcon()}
            {removable ? (
              <IconTrash
                className="text-red-700 hover:text-red-500 cursor-pointer w-6 h-6 ml-1.5 mr-0"
                onClick={() => removeRow(childItem)}
              />
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  /**
   * Workaround to handle different shapes of compliance. Somehow somewhere we are feeding the wrong shape.
   */
  const childWithCompliances = {
    ...childItem,
    compliant_conformities_names: childItem.compliance?.conformities,
    compliant_certifications_names: childItem.compliance?.certifications,
  } as ProductSearchOutput;

  if (
    "compliant_conformities_names" in childItem &&
    childItem["compliant_conformities_names"]
  ) {
    childWithCompliances["compliant_conformities_names"] = childItem[
      "compliant_conformities_names"
    ] as string[];
  }

  if (
    "compliant_certifications_names" in childItem &&
    childItem["compliant_certifications_names"]
  ) {
    childWithCompliances["compliant_certifications_names"] = childItem[
      "compliant_certifications_names"
    ] as string[];
  }

  return (
    <div className={clsx("bg-white p-3 w-full group/row", className)}>
      <ProductComponentInfo item={childWithCompliances} />
      {removable ? (
        hoverActions()
      ) : (
        <div className="absolute h-full right-0 top-0 flex items-center pr-3">
          {showDetailsIcon()}
        </div>
      )}
      <ProductDetailsPopup
        productId={childItem.id}
        productName={childItem.name}
        productType={childItem.type}
        open={openDetails}
        close={() => setOpenDetails(false)}
      ></ProductDetailsPopup>
    </div>
  );
};

export default observer(ComponentChildContent);
